import Axios from "axios";
import type { RemoteCateringMenu } from "../../../components/ServiceManagement/Catering/Domain/Types/Remote-CateringMenu.types";
import type { EquipmentInventory } from "../../FloorManager/typings/svg.types";
import type { BookableBookingObjectDto } from "../typings/resource-calendar.types";
import type { BookingResourceInfo } from "../typings/booking-resource-info.types";
import { createStubResourceInfoForResourceBooking } from "./createStubResourceInfoForResourceBooking";

function picturePathsFromSingle(path: string | null | undefined): string[] | null {
  if (!path) {
    return null;
  }

  return [path];
}

export function mapEquipmentToBookingResourceInfo(
  equipment: EquipmentInventory,
  row: BookableBookingObjectDto
): BookingResourceInfo {
  return {
    id: equipment.id,
    name: equipment.name,
    description: equipment.description ?? null,
    resourceKind: "equipment",
    locationName: equipment.locationName ?? equipment.location?.name ?? row.locationName,
    floorName: equipment.floorName ?? equipment.floor?.name ?? row.floorName,
    categoryName: equipment.equipmentCategory?.name ?? null,
    bookingLeadTime: equipment.bookingLeadTime ?? null,
    pricePerUnit: equipment.pricePerHour ?? null,
    picturePaths: picturePathsFromSingle(equipment.picturePath)
  };
}

export function mapCateringToBookingResourceInfo(
  catering: RemoteCateringMenu,
  row: BookableBookingObjectDto
): BookingResourceInfo {
  return {
    id: catering.id,
    name: catering.name,
    description: catering.description ?? null,
    resourceKind: "catering",
    locationName: row.locationName,
    floorName: row.floorName,
    categoryName: null,
    bookingLeadTime: catering.bookingLeadTime ?? null,
    pricePerUnit: catering.pricePerUnit ?? null,
    picturePaths: picturePathsFromSingle(catering.picturePath)
  };
}

async function fetchEquipmentInventory(
  equipmentId: number,
  locationInventoryId: number | null
): Promise<EquipmentInventory | null> {
  if (locationInventoryId != null) {
    const { data } = await Axios.get<EquipmentInventory[]>("/equipment-inventory/filtered", {
      params: { type: "location", ids: [locationInventoryId] }
    });
    const match = data.find(item => item.id === equipmentId);
    if (match) {
      return match;
    }
  }

  const { data } = await Axios.get<EquipmentInventory[]>("/equipment-inventory");
  return data.find(item => item.id === equipmentId) ?? null;
}

async function fetchCateringService(
  serviceId: number,
  locationInventoryId: number | null
): Promise<RemoteCateringMenu | null> {
  const { data } = await Axios.get<RemoteCateringMenu[]>("/booking-services", {
    params: locationInventoryId != null ? { locationId: locationInventoryId } : undefined
  });

  const match = data.find(item => item.id === serviceId);
  if (match) {
    return match;
  }

  if (locationInventoryId == null) {
    return null;
  }

  const { data: allServices } = await Axios.get<RemoteCateringMenu[]>("/booking-services");
  return allServices.find(item => item.id === serviceId) ?? null;
}

export async function fetchResourceInfoForConfirm(
  row: BookableBookingObjectDto
): Promise<BookingResourceInfo> {
  const stub = createStubResourceInfoForResourceBooking(row);
  const resourceKind = row.resourceKind ?? "equipment";

  try {
    if (resourceKind === "equipment") {
      const equipment = await fetchEquipmentInventory(
        row.bookingInventoryId,
        row.locationInventoryId
      );
      return equipment ? mapEquipmentToBookingResourceInfo(equipment, row) : stub;
    }

    const catering = await fetchCateringService(row.bookingInventoryId, row.locationInventoryId);
    return catering ? mapCateringToBookingResourceInfo(catering, row) : stub;
  } catch {
    return stub;
  }
}
